// Psychologist/PsychologistListWrapper.tsx

import React, {useCallback, useEffect, useRef, useState} from 'react';
import {IPsychologist} from '../../Types/psychology/psychologist';
import CircularProgress from 'Core/components/elements/CircularProgress';
import {useErrorProcessing} from 'Core/components/ErrorProvider';
import PsychologistItem from 'Psychologist/PsychologistItem';
import ScrollContainer from 'WideLayout/ScrollContainer';
import PsychologistDetail from 'Psychologist/PsychologistDetail';
import {FC, FCCC, FCSC, FRC, FRCC} from 'WideLayout/Layouts';
import {Message} from 'Core/components/Message';
import pprint from "../../Utils/pprint";

interface FetchPsychologistsResponse {
    results: IPsychologist[];
    count: number;
    next: string | null;
}

interface PsychologistListWrapperProps {
    fetchPsychologists: (page: number) => Promise<FetchPsychologistsResponse>;
    initialPage?: number;
    emptyMessage?: React.ReactNode;
    showChatButton?: boolean;
    classNameScrollBar?: string;
    classNameContainer?: string;
    gradientOverlaySx?: object;
    containerRef?: React.RefObject<HTMLDivElement>;
}

const PsychologistListWrapper: React.FC<PsychologistListWrapperProps> = (
    {
        fetchPsychologists,
        initialPage = 1,
        emptyMessage = <FRC mt={2}>Нет доступных психологов.</FRC>,
        showChatButton = false,
        classNameScrollBar = 'pt-3 pb-2 px-2 frss gap-2',
        classNameContainer = "maxw-700px mx-auto w-100",
        gradientOverlaySx = {zIndex: 8},
        containerRef,
    }
) => {
    const {byResponse} = useErrorProcessing();
    const [psychologists, setPsychologists] = useState<IPsychologist[]>([]);
    const [selectedPsychologist, setSelectedPsychologist] = useState<IPsychologist | null>(null);
    const [page, setPage] = useState<number>(initialPage);
    const [hasMore, setHasMore] = useState<boolean>(true);
    const [isFetching, setIsFetching] = useState<boolean>(false);
    const [initialLoading, setInitialLoading] = useState<boolean>(true);

    const scrollContainerRef = useRef<HTMLDivElement | null>(null);
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const fetchingRef = useRef(false);
    const messageShownRef = useRef(false);

    const [choosedId, setChoosedId] = useState<string | null>(() => {
        const params = new URLSearchParams(window.location.search);
        return params.get('choosed');
    });

    const removeChoosedParamFromUrl = useCallback(() => {
        const url = new URL(window.location.href);
        url.searchParams.delete('choosed');
        window.history.replaceState(null, '', url.toString());
    }, []);

    const loadPage = useCallback(async (currentPage: number) => {
        return await fetchPsychologists(currentPage);
    }, [fetchPsychologists]);

    const loadData = useCallback(async () => {
        fetchingRef.current = true;
        setIsFetching(true);

        try {
            let allPsychs: IPsychologist[] = [];
            let currentPage = initialPage;
            let found: IPsychologist | undefined;
            let nextUrl: string | null = null;
            let count = 0;

            do {
                const response = await loadPage(currentPage);
                const newPsychs = response.results;
                count = response.count;
                nextUrl = response.next;
                allPsychs = [...allPsychs, ...newPsychs];
                if (choosedId) {
                    found = allPsychs.find(p => p.id.toString() === choosedId);
                    if (found) break;
                }

                if (!choosedId) break;
                if (nextUrl) currentPage += 1;
            } while (choosedId && nextUrl);

            setPsychologists(allPsychs);
            setHasMore(nextUrl !== null);

            if (!messageShownRef.current && count > 0) {
                Message.info(`Мы подобрали для вас ${count} психологов по вашему запросу.`);
                messageShownRef.current = true;
            }

            if (choosedId && found) {
                setSelectedPsychologist(found);
                removeChoosedParamFromUrl();
                setChoosedId(null);
            } else if (choosedId && !found) {
                removeChoosedParamFromUrl();
                setChoosedId(null);
                if (allPsychs.length > 0) {
                    if (!selectedPsychologist) setSelectedPsychologist(allPsychs[0]);
                }
            } else if (!choosedId) {
                if (!selectedPsychologist && allPsychs.length > 0) {
                    setSelectedPsychologist(allPsychs[0]);
                }
            }

            setPage(currentPage);
        } catch (error) {
            byResponse(error);
        } finally {
            setIsFetching(false);
            fetchingRef.current = false;
            setInitialLoading(false);
        }
    }, [
        choosedId,
        initialPage,
        loadPage,
        removeChoosedParamFromUrl,
        byResponse,
        // selectedPsychologist Это убьет все нахрен
    ]);

    useEffect(() => {
        loadData().then();
    }, [loadData]);

    const handleScroll = useCallback(() => {
        if (!scrollContainerRef.current || isFetching || !hasMore || fetchingRef.current) {
            return;
        }

        const {scrollLeft, scrollWidth, clientWidth} = scrollContainerRef.current;
        const distToRight = scrollWidth - scrollLeft - clientWidth;

        if (distToRight < 200) {
            const nextPage = page + 1;
            fetchingRef.current = true;
            setIsFetching(true);

            loadPage(nextPage).then(response => {
                const newPsychs = response.results;
                const updatedList = [...psychologists, ...newPsychs];
                setPsychologists(updatedList);
                setHasMore(response.next !== null);
                setIsFetching(false);
                fetchingRef.current = false;
                setPage(nextPage);
            }).catch(error => {;
                byResponse(error);
                setIsFetching(false);
                fetchingRef.current = false;
            });
        }
    }, [page, isFetching, hasMore, loadPage, psychologists, byResponse]);

    useEffect(() => {
        const container = scrollContainerRef.current;
        if (container) {
            container.addEventListener('scroll', handleScroll);
            return () => {
                container.removeEventListener('scroll', handleScroll);
            };
        }
    }, [handleScroll]);

    if (initialLoading && psychologists.length === 0) {
        return (
            <FCCC h={'80%'}>
                <CircularProgress size={'150px'}/>
            </FCCC>
        );
    }

    if (!initialLoading && psychologists.length === 0) {
        return (
            <>
                {emptyMessage}
            </>
        );
    }

    return (
        <FC cls={'psychologists-wrapper no-scrollbar'}
            pb={6} pos={'relative'} ref={wrapperRef} grow
            scroll={'y-auto'} maxW={'100vw'}>
            <ScrollContainer

                ref={scrollContainerRef}
                enableArrows={true}
                slideElCount={3}
                direction="horizontal"
                className={classNameContainer}
                classNameScrollBar={classNameScrollBar}
                gradientOverlaySx={gradientOverlaySx}>
                {psychologists.map((psychologist) => (
                    <PsychologistItem
                        isActive={selectedPsychologist?.id === psychologist.id}
                        key={psychologist.id}
                        psychologist={psychologist}
                        showChatButton={showChatButton}
                        onClick={() => {
                            pprint(`[onClick] Выбран психолог ID=${psychologist.id}`);
                            setSelectedPsychologist(psychologist);
                        }}
                    />
                ))}
                {isFetching && hasMore && (
                    <FRCC my={'auto'} minW={'180px'}>
                        <CircularProgress size={50}/>
                    </FRCC>
                )}
            </ScrollContainer>
            {selectedPsychologist
                ? <FCSC>
                    <PsychologistDetail
                        className={'w-100'}
                        key={selectedPsychologist.id}
                        id={selectedPsychologist.user.id}
                        containerRef={containerRef ? containerRef : wrapperRef}
                    />
                </FCSC>
                : <FRC mt={2}>Это странно, но тут ничего нет :)</FRC>
            }
        </FC>
    );
};

export default PsychologistListWrapper;
